<template>
  <div>
    <!-- Also covers the step-4 → "return to configuration" path, where no refetch runs -->
    <div
      v-if="checkoutStore.soldOutBanner"
      role="alert"
      class="mb-4 rounded-lg border border-danger-fg/30 bg-danger-fg/20 px-4 py-3 text-danger-fg"
    >
      {{ checkoutStore.soldOutBanner }}
    </div>

    <!-- Error message -->
    <div
      v-if="checkoutStore.error"
      class="mb-4 flex flex-col gap-2 rounded-lg border border-danger-fg/30 bg-danger-fg/20 px-4 py-3 text-danger-fg sm:flex-row sm:items-center sm:justify-between"
    >
      {{ checkoutStore.error }}
      <button
        type="button"
        class="shrink-0 self-start rounded-full border border-danger-fg px-3 py-1.5 uppercase transition-colors hover:bg-danger-fg/10 disabled:opacity-50 sm:self-auto"
        :disabled="checkoutStore.isLoading"
        @click="retryOrderInformation"
      >
        {{ t('general/retry') }}
      </button>
    </div>

    <!-- Loading state before any content arrives -->
    <div
      v-if="!hasContent && !checkoutStore.error && !hasAnySection"
      class="flex flex-col items-center justify-center gap-4 py-24"
    >
      <div
        class="h-8 w-8 animate-spin rounded-full border-2 border-depth-3 border-t-brand"
      ></div>
      <p class="text-mouse">
        {{ t('checkout/configure/loading') }}
      </p>
    </div>

    <div v-else class="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
      <!-- Left column: configuration form -->
      <div class="lg:col-span-8">
        <div class="flex flex-col gap-6">
          <!-- Custom components from Strapi (e.g. Cfx.re info box) -->
          <checkout-custom-component
            v-for="c in checkoutStore.product?.customComponents ?? []"
            :key="`${c.component}__${c.id}`"
            :component="c"
          />

          <!-- 1. Unit selector (slots / RAM) -->
          <transition :name="hasMounted ? 'fade-slide' : undefined">
            <div v-if="shownSections.units" ref="unitRef">
              <checkout-unit-selector />
            </div>
          </transition>

          <!-- 2. Duration selector -->
          <transition :name="hasMounted ? 'fade-slide' : undefined">
            <div v-if="shownSections.duration" ref="durationRef">
              <checkout-duration-selector />
            </div>
          </transition>

          <!-- 3. Location selector -->
          <transition :name="hasMounted ? 'fade-slide' : undefined">
            <div v-if="shownSections.location" ref="locationRef">
              <checkout-location-selector />
            </div>
          </transition>

          <!-- 4. Addon list -->
          <transition :name="hasMounted ? 'fade-slide' : undefined">
            <div v-if="shownSections.addons" ref="addonRef">
              <checkout-addon-list />
            </div>
          </transition>

          <!-- Mobile: coupon + price + continue (hidden on lg+) -->
          <template v-if="showSidebar">
            <div class="flex flex-col gap-6 lg:hidden">
              <checkout-price-breakdown />

              <checkout-balance-migration-box v-if="!isDesktop" />

              <!-- Mobile Continue button (in-page) -->
              <common-button
                v-if="hasContent"
                ref="mobileContinueRef"
                block
                :disabled="!canContinue"
                @click="goToNextStep"
              >
                {{ t('checkout/configure/continue') }}
              </common-button>
            </div>
          </template>

          <!-- Trust & payment info -->
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <!-- Advantages -->
            <checkout-section-card>
              <h3 class="mb-4 text-lg font-semibold text-mouse">
                {{ t('checkout/configure/advantages/title') }}
              </h3>
              <ul class="flex flex-col gap-2.5">
                <li
                  v-for="item in advantages"
                  :key="item"
                  class="flex items-start gap-2 text-rhino"
                >
                  <fa-icon
                    :icon="faSquareCheck"
                    class="mt-1 size-5 shrink-0 text-brand"
                  />
                  {{ item }}
                </li>
              </ul>
            </checkout-section-card>

            <!-- Secure payment -->
            <checkout-section-card>
              <h3 class="mb-4 text-lg font-semibold text-mouse">
                {{ t('checkout/configure/payment/title') }}
              </h3>
              <div class="grid grid-cols-2 gap-4">
                <img
                  v-for="pm in paymentOptions"
                  :key="pm"
                  :src="`/sc/images/svgs/payment/${pm}.svg`"
                  :alt="pm"
                  class="h-20 w-full object-contain"
                />
              </div>
            </checkout-section-card>
          </div>
        </div>
      </div>

      <!-- Right column: sticky sidebar (desktop only) -->
      <div v-if="showSidebar" class="hidden lg:block lg:col-span-4">
        <div class="sticky top-8 flex flex-col gap-6">
          <checkout-price-breakdown />

          <checkout-balance-migration-box v-if="isDesktop" />

          <!-- Continue button (desktop, under summary) -->
          <common-button
            v-if="hasContent"
            block
            :disabled="!canContinue"
            @click="goToNextStep"
          >
            {{ t('checkout/configure/continue') }}
          </common-button>
        </div>
      </div>
    </div>

    <!-- Mobile sticky bar: only when in-page button is not visible -->
    <transition name="slide-up">
      <div
        v-if="showSidebar && hasContent && !mobileContinueVisible"
        class="fixed inset-x-0 bottom-0 z-30 border-t border-white/5 bg-depth-0 px-4 py-3 lg:hidden"
      >
        <div
          class="mx-auto flex max-w-screen-xl items-center justify-between gap-4"
        >
          <div class="min-w-0">
            <p class="text-rhino">
              {{ t('checkout/configure/price/total') }}
            </p>
            <p class="truncate text-lg font-bold text-brand">
              {{ totalFormatted }}
            </p>
          </div>
          <common-button
            class="shrink-0"
            :disabled="!canContinue"
            @click="goToNextStep"
          >
            {{ t('checkout/configure/continue') }}
          </common-button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { faSquareCheck } from '@fortawesome/pro-regular-svg-icons';
import type { ComponentPublicInstance } from 'vue';
import { trackAddToCart } from '~/helpers/gaEcommerce';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const checkoutStore = useCheckoutStore();
const { format } = useNumberMoneyFormatting();

const advantages = computed(() => [
  t('checkout/configure/advantages/community'),
  t('checkout/configure/advantages/support'),
  t('checkout/configure/advantages/interface'),
  t('checkout/configure/advantages/flexibility'),
  t('checkout/configure/advantages/fast_setup'),
  t('checkout/configure/advantages/secure_payment'),
]);

const paymentOptions = ['visa', 'mastercard', 'amex', 'paypal'];

const durationRef = ref<HTMLElement>();
const locationRef = ref<HTMLElement>();
const unitRef = ref<HTMLElement>();
const addonRef = ref<HTMLElement>();
const mobileContinueRef = ref<ComponentPublicInstance | null>(null);
// One balance box, in whichever column is showing: each one asks the server.
const isDesktop = useAppBreakpoints().greaterOrEqual('lg');
const mobileContinueVisible = ref(true);

const hasMounted = ref(false);
onMounted(() => {
  nextTick(() => {
    hasMounted.value = true;
  });
});

const { stop: stopObserver } = useIntersectionObserver(
  mobileContinueRef,
  ([entry]) => {
    mobileContinueVisible.value = entry?.isIntersecting ?? true;
  },
  { threshold: 0.1 },
);
onUnmounted(() => stopObserver());

const shownSections = reactive({
  duration: false,
  location: false,
  units: false,
  addons: false,
});

// Latched so the section's own skeleton and empty state can render.
function bindSection(
  key: keyof typeof shownSections,
  hasData: () => boolean,
  whileLoading = false,
): void {
  watch(
    [hasData, () => checkoutStore.isLoading],
    ([data, loading]) => {
      shownSections[key] =
        data || (whileLoading && (loading || shownSections[key]));
    },
    { immediate: true },
  );
}

bindSection('units', () => checkoutStore.units !== null, true);
bindSection('duration', () => checkoutStore.durations.length > 0, true);
bindSection('location', () => checkoutStore.locations.length > 0, true);
bindSection('addons', () => checkoutStore.addons.length > 0);

// Once a section is mounted it owns the loading/empty state, so the page spinner steps aside.
const hasAnySection = computed<boolean>(
  () => shownSections.units || shownSections.duration || shownSections.location,
);

const hasContent = computed<boolean>(
  () =>
    checkoutStore.locations.length > 0 ||
    checkoutStore.durations.length > 0 ||
    checkoutStore.units !== null,
);

const canContinue = computed<boolean>(
  () =>
    checkoutStore.complete &&
    checkoutStore.price !== null &&
    !checkoutStore.regionMismatch &&
    !checkoutStore.selectedLocationSoldOut,
);

const sidebarEverShown = ref(false);
watch(
  () =>
    checkoutStore.price !== null ||
    checkoutStore.couponsAvailable ||
    checkoutStore.autoAppliedCodes.length > 0,
  (v) => {
    if (v) sidebarEverShown.value = true;
  },
  { immediate: true },
);
const showSidebar = computed<boolean>(
  () => sidebarEverShown.value || checkoutStore.isLoading,
);

const totalFormatted = computed<string>(() => {
  if (!checkoutStore.price?.total) return '—';
  return format(checkoutStore.price.total);
});

/**
 * Returns the ref of the next section that needs user attention,
 * based on what's currently missing in the store.
 */
function getNextIncompleteRef(): HTMLElement | undefined {
  // Priority follows visual page order: units → duration → location → addons
  if (checkoutStore.units !== null && checkoutStore.selectedUnits === null) {
    return unitRef.value;
  }
  if (
    checkoutStore.durations.length > 0 &&
    checkoutStore.selectedDuration === null
  ) {
    return durationRef.value;
  }
  if (
    checkoutStore.locations.length > 0 &&
    checkoutStore.selectedLocationId === null
  ) {
    return locationRef.value;
  }
  // No addon fallback: a re-init fills every section, which dragged the page to the bottom.
  return undefined;
}

function scrollToNextIncomplete(): void {
  nextTick(() => {
    const target = getNextIncompleteRef();
    if (!target) return;

    const mobileBarHeight = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue(
        '--mobile-bar-height',
      ),
    );
    const offset = (mobileBarHeight || 4.5) * 16 + 16;
    const top = target.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({ top, behavior: 'smooth' });
  });
}

// Scroll only on first selection — re-selecting a value should not re-scroll
watch(
  () => checkoutStore.selectedDuration,
  (val, old) => {
    if (val !== null && old === null) scrollToNextIncomplete();
  },
);

watch(
  () => checkoutStore.selectedLocationId,
  (val, old) => {
    if (val !== null && old === null) scrollToNextIncomplete();
  },
);

watch(
  () => checkoutStore.selectedUnits,
  (val, old) => {
    if (val !== null && old === null) scrollToNextIncomplete();
  },
);

watch(
  () => checkoutStore.locations.length,
  (len, oldLen) => {
    if (len > 0 && oldLen === 0) scrollToNextIncomplete();
  },
);

watch(
  () => checkoutStore.units,
  (units, oldUnits) => {
    if (units && !oldUnits) scrollToNextIncomplete();
  },
);

watch(
  () => checkoutStore.addons.length,
  (len, oldLen) => {
    if (len > 0 && oldLen === 0) scrollToNextIncomplete();
  },
);

function retryOrderInformation(): void {
  checkoutStore.fetchOrderInformation();
}

function goToNextStep(): void {
  if (!canContinue.value) return;
  router.push({
    params: { ...route.params, stepNumber: '2' },
    query: route.query,
  });
}

// Keyed so a currency/plan switch retracks, but a plain price refetch does not.
const trackedAddToCartKey = ref<string | null>(null);
watch(
  () => {
    const total = checkoutStore.price?.total;
    const productKey = checkoutStore.productKey;
    const planId = checkoutStore.planId;
    if (!total || !productKey || !planId) return null;
    return `${planId}__${productKey}__${total.currencyCode}`;
  },
  (key) => {
    if (!import.meta.client || !key || key === trackedAddToCartKey.value)
      return;
    const total = checkoutStore.price?.total;
    const productKey = checkoutStore.productKey;
    if (!total || !productKey) return;
    trackAddToCart({
      productKey,
      name: checkoutStore.product?.name ?? checkoutStore.product?.title,
      total,
      coupon: checkoutStore.couponCodes.join(',') || undefined,
    });
    trackedAddToCartKey.value = key;
  },
  { immediate: true },
);
</script>

<style scoped>
.fade-slide-enter-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}
.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.25s ease;
}
.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}
</style>
